import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { i18n } from 'i18n';
import { AccessType, SearchBox } from './autoAssignUtils';
import { ExistingStopeBehavior } from './bulkImportSolidsSceneState';

interface WizardStepSearchParamsProps {
  accessType: AccessType;
  searchBox: SearchBox;
  existingStopeBehavior: ExistingStopeBehavior;
  onChangeSearchBox: (box: SearchBox) => void;
  onChangeExistingStopeBehavior: (behavior: ExistingStopeBehavior) => void;
  onBack: () => void;
  onConfirm: () => void;
}

const WizardStepSearchParams = ({
  accessType,
  searchBox,
  existingStopeBehavior,
  onChangeSearchBox,
  onChangeExistingStopeBehavior,
  onBack,
  onConfirm,
}: WizardStepSearchParamsProps) => {
  const verticalValue = accessType === 'top' ? searchBox.plusZ : searchBox.minusZ;
  const verticalLabel = accessType === 'top'
    ? i18n._('Vertical search (metres above stope top)')
    : i18n._('Vertical search (metres below stope bottom)');

  const handleVerticalChange = (value: string) => {
    const num = parseFloat(value);
    if (Number.isNaN(num) || num < 0) return;
    if (accessType === 'top') {
      onChangeSearchBox({ ...searchBox, plusZ: num });
    } else {
      onChangeSearchBox({ ...searchBox, minusZ: num });
    }
  };

  const handleFallbackDepthChange = (value: string) => {
    const num = parseFloat(value);
    if (Number.isNaN(num) || num < 0) return;
    onChangeSearchBox({ ...searchBox, minusZ: num });
  };

  const handleHorizontalXChange = (value: string) => {
    const num = parseFloat(value);
    if (Number.isNaN(num) || num < 0) return;
    onChangeSearchBox({ ...searchBox, plusX: num, minusX: num });
  };

  const handleHorizontalYChange = (value: string) => {
    const num = parseFloat(value);
    if (Number.isNaN(num) || num < 0) return;
    onChangeSearchBox({ ...searchBox, plusY: num, minusY: num });
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">{i18n._('Search Parameters')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {i18n._('Configure how far to search from each solid for candidate nodes and spools.')}
      </Typography>

      <TextField
        label={verticalLabel}
        type="number"
        size="small"
        color="secondary"
        value={verticalValue}
        onChange={(e) => handleVerticalChange(e.target.value)}
        inputProps={{ min: 0, step: 0.5 }}
      />

      {accessType === 'top' && (
        <TextField
          label={i18n._('Fallback search (metres below stope top)')}
          helperText={i18n._('Used only when nothing is found above the stope. Set to 0 to turn off.')}
          type="number"
          size="small"
          color="secondary"
          value={searchBox.minusZ}
          onChange={(e) => handleFallbackDepthChange(e.target.value)}
          inputProps={{ min: 0, step: 0.5 }}
        />
      )}

      <TextField
        label={i18n._('Horizontal X search (metres, symmetric)')}
        type="number"
        size="small"
        color="secondary"
        value={searchBox.plusX}
        onChange={(e) => handleHorizontalXChange(e.target.value)}
        inputProps={{ min: 0, step: 0.5 }}
      />

      <TextField
        label={i18n._('Horizontal Y search (metres, symmetric)')}
        type="number"
        size="small"
        color="secondary"
        value={searchBox.plusY}
        onChange={(e) => handleHorizontalYChange(e.target.value)}
        inputProps={{ min: 0, step: 0.5 }}
      />

      <FormControl>
        <FormLabel>{i18n._('If a stope with the same name already exists')}</FormLabel>
        <RadioGroup
          value={existingStopeBehavior}
          onChange={(e) => onChangeExistingStopeBehavior(e.target.value as ExistingStopeBehavior)}
        >
          <FormControlLabel value="skip" control={<Radio color="secondary" />} label={i18n._('Skip this solid')} />
          <FormControlLabel value="update" control={<Radio color="secondary" />} label={i18n._('Update the existing stope')} />
        </RadioGroup>
      </FormControl>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="outlined" color="secondary" onClick={onBack} fullWidth>
          {i18n._('Back')}
        </Button>
        <Button variant="contained" color="secondary" onClick={onConfirm} fullWidth>
          {i18n._('Run Auto-Assign')}
        </Button>
      </Box>
    </Box>
  );
};

export default WizardStepSearchParams;
